import { IScope, IPromise, IRootScopeService, IFilterService } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IModalService } from "angular-ui-bootstrap";
import { IStateInit } from "@gtmhub/core/routing";
import { IIndicator } from "@gtmhub/error-handling";
import { ICustomField, CustomFieldActions, ICustomFieldsStoreState, CustomFieldService } from "@gtmhub/customFields";
import { IGridCustomField, ISessionTag } from "@gtmhub/customFields/models/models";
import { INgRedux } from "@gtmhub/state-management";
import {
  columnsFieldLabelMap,
  convertColumnToDefinition,
  getCustomFieldGHGridColumns,
  defaultCustomFieldGHGridColumns,
  getMappedVisualizationKind,
  convertColumnToSortingDefinition,
  sortCustomFieldsBySessionCount,
} from "@gtmhub/customFields/utils";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { IPluginsStoreState, filterPluginsByType, IPlugin, PluginsActions } from "@gtmhub/plugins";
import { GridFilterDefinitions, IGridFiltersChange, OrFiltersGroup } from "@gtmhub/shared/components/grid-filtering-bar/grid-filters/models";
import { getPersistedScreenSettings, setPersistedScreenSettings } from "@gtmhub/shared/components/grid-filtering-bar/persisting/models";
import { customFieldsFilterDefinitions, generateDefaultCustomFieldsFilters, initializeCustomFieldsRequest } from "../filter-utils";
import {
  GridColumnDefinitions,
  GridSortingDefinitions,
  IGridSortingDefinition,
  IGridSortingRule,
  toGridDefinitionMap,
} from "@gtmhub/shared/components/grid-filtering-bar";
import { IGHCellChangePayload, IGHCellClickPayload, IGHGridColumn } from "@gtmhub/shared/components/grid/grid.component";
import { NameMap, toKeyMap, capitalize } from "@gtmhub/util";
import { localize } from "@gtmhub/localization";
import { QuerySessionsService } from "../services/query-sessions.service";
import { hasFiltersGroupsWithValues } from "@gtmhub/goals/filter-utils";
import { ISessionsStoreState } from "@gtmhub/sessions";
import { CustomFieldModalService } from "@gtmhub/customFields/custom-fields-modal.service";

const bindStateToScope = (state: ICustomFieldsStoreState & ISessionsStoreState & IPluginsStoreState): Record<string, unknown> => {
  return {
    plugins: filterPluginsByType(state.plugins.items, "ui"),
    customFields: Object.values(state.customFields.map).reduce((customFields, typeCfArr) => ((customFields = [...customFields, ...typeCfArr]), customFields), []),
    indicators: {
      getting: state.customFields.fetching,
      creating: state.customFields.creating,
      updating: state.customFields.updating,
      deleting: state.customFields.deleting,
    },
  };
};

export interface ICustomFieldsScope extends IScope, IExtendedRootScopeService {
  plugins: IPlugin[];
  indicators: {
    getting?: IIndicator;
    creating?: IIndicator;
    updating?: IIndicator;
    deleting?: IIndicator;
    loading?: boolean;
    reloading?: boolean;
  };
  customFields: ICustomField[];
  gridCustomFields: IGridCustomField[];
  filters: OrFiltersGroup[];
  filterDefinitions: GridFilterDefinitions;
  columnDefinitions: GridColumnDefinitions;
  gridColumns: IGHGridColumn[];
  sortingDefinitions: GridSortingDefinitions;
  sortingRules: IGridSortingRule[];
  columns: IGHGridColumn[];
  currentPage: number;
  totalPages: number;
  onPageChange(page: number): void;
  onActionButtonClick(): void;
  showEmptyBlock: boolean;
  showNoFilterResults: boolean;
  filtersChange(change: IGridFiltersChange): void;
  sortingChange(): void;
  columnsChange(): void;
  updateGrid(payload?: IGHCellChangePayload<Partial<IGridCustomField>>): void;
  confirmDeleteCustomField(index: string);
  resetIndicator(indicator: keyof ICustomFieldsScope["indicators"]): void;
  findPluginByPluginVersionId(pluginVersionId: string): IPlugin;
  loadCustomFieldModal(payload?: IGHCellClickPayload<IGridCustomField>): void;
}

export interface ICustomFieldsStateParams {
  page?: string;
}

const SCREEN_PATH = ["custom-fields"];
const CUSTOM_FIELDS_GRID_COLUMNS = "customFieldsGridColumns";
const PAGING_SIZE = 40;
const FIRST_PAGE = 1;
const LABEL = "label";
const ACTIONS = "actions";
const SESSION = "session";

export class CustomFieldsCtrl implements IStateInit {
  private columnsFieldMap: NameMap<IGHGridColumn>;
  private customFieldsTotalCount: number;

  public static $inject = [
    "$scope",
    "$rootScope",
    "$state",
    "$stateParams",
    "CustomFieldActions",
    "$uibModal",
    "$ngRedux",
    "PluginActions",
    "$filter",
    "QuerySessionsService",
    "CustomFieldService",
    "CustomFieldModalService",
  ];
  constructor(
    private $scope: ICustomFieldsScope,
    private $rootScope: IRootScopeService & ITraceRootScopeService,
    private $state: StateService,
    private $stateParams: ICustomFieldsStateParams,
    private customFieldsActions: CustomFieldActions,
    private $uibModal: IModalService,
    private $ngRedux: INgRedux,
    private pluginActions: PluginsActions,
    private $filter: IFilterService,
    private querySessionsService: QuerySessionsService,
    private customFieldService: CustomFieldService,
    private customFieldModal: CustomFieldModalService
  ) {
    this.$scope.indicators = {};
    this.$rootScope.$broadcast("configurationChildStateLoaded", this.$state.$current.name);

    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);

    this.$scope.onPageChange = this.onPageChange;
    this.$scope.filtersChange = this.filtersChange;
    this.$scope.sortingChange = this.sortingChange;
    this.$scope.columnsChange = this.columnsChange;
    this.$scope.updateGrid = this.updateGrid;
    this.$scope.loadCustomFieldModal = this.loadCustomFieldModal;
    this.$scope.confirmDeleteCustomField = this.confirmDeleteCustomField;
    this.$scope.resetIndicator = this.resetIndicator;
    this.$scope.findPluginByPluginVersionId = this.findPluginByPluginVersionId;
    this.$scope.onActionButtonClick = this.onActionButtonClick;
  }

  public stateInit(): IPromise<unknown> {
    this.$ngRedux.dispatch(this.pluginActions.getPlugins());
    this.$ngRedux.dispatch(this.customFieldsActions.getCustomFields());

    this.initColumns();
    this.initFilters();
    this.initSorting();
    this.$scope.filterDefinitions = customFieldsFilterDefinitions();

    const paramsPage = Number(this.$stateParams.page);
    this.$scope.currentPage = isNaN(paramsPage) ? FIRST_PAGE : paramsPage;

    return this.$scope
      .watchUntil(() => !this.$scope.indicators.getting)
      .then(() => {
        this.getCustomFields();
        this.watchCustomFields();
      });
  }

  private initColumns = (): void => {
    this.$scope.columns = getCustomFieldGHGridColumns(SCREEN_PATH);
    this.$scope.columns.forEach((col) => {
      col.label = capitalize(localize(columnsFieldLabelMap[col.fieldName]));
    });
    this.columnsFieldMap = toKeyMap("fieldName", this.$scope.columns);
    this.$scope.columnDefinitions = this.createColumnDefinitions(this.$scope.columns);
    this.$scope.gridColumns = this.$scope.columns.filter((column: IGHGridColumn): boolean => column.visible);
  };

  private initFilters = (): void => {
    const persistedFilters = getPersistedScreenSettings(SCREEN_PATH, "filters") || [];

    if (persistedFilters.length) {
      this.$scope.filters = persistedFilters;
    } else {
      this.$scope.filters = generateDefaultCustomFieldsFilters();
    }
  };

  private initSorting = (): void => {
    this.$scope.sortingDefinitions = this.createSortingDefinitions();
    this.generateSortingRules();
  };

  private watchCustomFields = (): void => {
    this.$scope.$watch("customFields", (newValue: ICustomField[], oldValue: ICustomField[]) => {
      // equality check needed to prevent infinite loop since we update customFields to distinct customFields
      if (!angular.equals(newValue, oldValue)) {
        this.getCustomFields();
      }
    });
  };

  private getCustomFields = (): Promise<void> => {
    const skip = (this.$scope.currentPage - 1) * PAGING_SIZE;
    const customFieldsRequest = initializeCustomFieldsRequest(this.$scope.filters, this.$scope.sortingRules);
    customFieldsRequest.skip = skip;
    customFieldsRequest.limit = PAGING_SIZE;
    this.$scope.indicators.reloading = true;

    return this.customFieldService.getCustomFieldsV2<ICustomField>(customFieldsRequest).then((result) => {
      this.customFieldsTotalCount = result.totalCount;

      this.setTotalPages(this.customFieldsTotalCount);
      this.$scope.indicators.reloading = false;
      this.$scope.customFields = result.items || [];

      this.setGridCustomFields(customFieldsRequest.sort);
      this.setEmptyScreenAndNoFilterDisplay();
    });
  };

  private createColumnDefinitions = (columns: IGHGridColumn[]): GridColumnDefinitions => {
    return toGridDefinitionMap(columns.filter((column) => column.fieldName !== ACTIONS).map((column) => convertColumnToDefinition(column, this.$filter)));
  };

  private createSortingDefinitions = (): Record<string, IGridSortingDefinition> => {
    return toGridDefinitionMap(
      this.$scope.columns
        .filter((column) => column.fieldName !== "targetTypes" && column.fieldName !== ACTIONS && column.visible)
        .map((column) => convertColumnToSortingDefinition(column, this.$filter))
    );
  };

  private generateSortingRules = (): void => {
    const persistedSortingRules: IGridSortingRule[] = getPersistedScreenSettings(SCREEN_PATH, "sorting");

    if (persistedSortingRules) {
      this.$scope.sortingRules = persistedSortingRules.filter((sortingRule) => sortingRule.fieldName in this.$scope.sortingDefinitions);
    } else {
      this.$scope.sortingRules = [{ fieldName: "dateCreated", reversed: true }];
    }
  };

  private filtersChange = (change: IGridFiltersChange): void => {
    this.$rootScope.traceAction("change_custom_fields_filters", () => {
      if (change.initial) {
        this.setCurrentPage(this.$scope.currentPage);
      } else {
        setPersistedScreenSettings(SCREEN_PATH, "filters", this.$scope.filters);
        this.setCurrentPage(FIRST_PAGE);
      }
    });
  };

  private sortingChange = (): void => {
    this.$rootScope.traceAction("change_custom_fields_sorting", () => {
      setPersistedScreenSettings(SCREEN_PATH, "sorting", this.$scope.sortingRules);
      this.setCurrentPage(FIRST_PAGE);
    });
  };

  private columnsChange = (): void => {
    this.$rootScope.traceAction("change_custom_field_columns", () => {
      this.$scope.columns = this.$scope.gridColumns.map((gridColumn) => ({ ...this.columnsFieldMap[gridColumn.fieldName], visible: true }));
      // actions column is always present
      this.$scope.columns.push(defaultCustomFieldGHGridColumns().find((column) => column.fieldName === ACTIONS));
      setPersistedScreenSettings(SCREEN_PATH, CUSTOM_FIELDS_GRID_COLUMNS, this.$scope.columns);
      this.$scope.sortingDefinitions = this.createSortingDefinitions();

      if (this.areSortingRulesMissingFromSortingDefinitions()) {
        this.generateSortingRules();
        this.sortingChange();
      }
    });
    this.setCurrentPage(FIRST_PAGE);
  };

  private areSortingRulesMissingFromSortingDefinitions = (): boolean => {
    const rulesNotInDefinitions = this.$scope.sortingRules.filter((sortingRule) => !(sortingRule.fieldName in this.$scope.sortingDefinitions));
    return rulesNotInDefinitions.length > 0;
  };

  private setTotalPages = (totalCount: number) => {
    this.$scope.totalPages = Math.ceil(totalCount / PAGING_SIZE);

    if (this.$scope.totalPages && this.$scope.totalPages < this.$scope.currentPage) {
      this.setCurrentPage(this.$scope.totalPages);
    }
  };

  private setCurrentPage = (page?: number) => {
    this.$scope.currentPage = page || FIRST_PAGE;
    this.goToCurrentPage();
  };

  private goToCurrentPage = () => {
    this.$state.go(".", { page: this.$scope.currentPage }, { notify: false });
    this.getCustomFields();
  };

  private setEmptyScreenAndNoFilterDisplay = (): void => {
    const noCustomFieldsFetched = !this.$scope.indicators.reloading && this.customFieldsTotalCount === 0;
    const hasAnyFilterWithValue = hasFiltersGroupsWithValues(this.$scope.filters);
    this.$scope.showEmptyBlock = noCustomFieldsFetched && !hasAnyFilterWithValue;
    this.$scope.showNoFilterResults = noCustomFieldsFetched && hasAnyFilterWithValue;
  };

  private setGridCustomFields = (sortBy: string) => {
    this.$scope.gridCustomFields = this.$scope.customFields.map((cf: ICustomField) => {
      return {
        id: cf._id,
        name: cf.name,
        label: cf.label,
        targetTypes: cf.targetTypes[0],
        visualizationKind: getMappedVisualizationKind(cf.visualizationKind),
        operationalType: cf.operationalType,
        dateCreated: cf.dateCreated,
        uniqueKey: cf.name,
        lastModified: cf.lastModified,
        description: cf.description,
        pluginVersionId: cf.pluginVersionId,
        pluginTitle: this.findPluginByPluginVersionId(cf.pluginVersionId)?.title,
        actions: "action button",
        sessions: this.populateSessions(cf),
      };
    });

    // Cover case when there are no sessions linked to the given custom field for types 'user', 'team' and 'task'.
    // https://gtmhub.atlassian.net/browse/GVS-14521
    if (sortBy === "-operationalType" || sortBy === "operationalType") {
      this.$scope.gridCustomFields = sortCustomFieldsBySessionCount(this.$scope.gridCustomFields, sortBy);
    }
  };

  private populateSessions = (customField: ICustomField): ISessionTag[] => {
    const sessionTags: ISessionTag[] = [];
    if (customField.operationalType === SESSION && customField.targetIds && customField.targetIds.length > 0) {
      customField.targetIds.forEach((targetId) => sessionTags.push(this.querySessionsService.createSessionTag(targetId)));
    }
    return sessionTags;
  };

  private onPageChange = (page: number): void => {
    this.setCurrentPage(page);
  };

  private onActionButtonClick = (): void => {
    this.customFieldModal.open(null);
  };

  private updateGrid = (payload?: IGHCellChangePayload<IGridCustomField>): void => {
    const { item, action } = payload || {};

    if (item && action) {
      const actionsMap = {
        delete: this.findIndexAndConfirmDelete,
        edit: this.openCustomFieldForm,
      };

      actionsMap[action](item);
    } else {
      setPersistedScreenSettings(SCREEN_PATH, CUSTOM_FIELDS_GRID_COLUMNS, this.$scope.columns);
    }
  };

  private openCustomFieldForm = (item: IGridCustomField) => {
    this.customFieldModal.open(item?.id);
  };

  private loadCustomFieldModal = (payload?: IGHCellClickPayload<IGridCustomField>): void => {
    const { item, column } = payload || {};
    if (column.fieldName !== LABEL) return;
    this.customFieldModal.open(item?.id);
  };

  private findIndexAndConfirmDelete = (item: IGridCustomField) => {
    for (let i = 0; i < this.$scope.customFields.length; i++) {
      if (this.$scope.customFields[i]._id === item.id) {
        this.confirmDeleteCustomField(i.toString());
        break;
      }
    }
  };

  private findPluginByPluginVersionId = (pluginVersionId: string): IPlugin => {
    return this.$scope.plugins.find((p) => p.pluginVersionId === pluginVersionId);
  };

  private confirmDeleteCustomField = (index: string) => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$uibModal.open({
      templateUrl: require("../views/confirm-delete.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        "$uibModalInstance",
        function ($scope, $uibModalInstance) {
          if (index != null && ctrl.$scope.customFields[index].targetIds) {
            $scope.sessionCount = ctrl.$scope.customFields[index].targetIds.length;
          } else {
            $scope.sessionCount = 0;
          }

          $scope.confirm = () => {
            ctrl.delete(index);
            $uibModalInstance.close();
          };

          $scope.close = () => {
            $uibModalInstance.close();
          };
        },
      ],
    });
  };

  private resetIndicator = (indicator: keyof ICustomFieldsScope["indicators"]): void => {
    this.$ngRedux.dispatch(this.customFieldsActions.resetCustomFieldIndicators(indicator));
  };

  private delete = (index: string) => {
    this.$ngRedux.dispatch(this.customFieldsActions.deleteCustomField(this.$scope.customFields[index]._id));
  };
}
